import React, { useState } from 'react';
import { MessageSquare, AlertCircle, ThumbsUp, ThumbsDown, Plus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import FeedbackList from '../components/feedback/FeedbackList';
import FeedbackFilters from '../components/feedback/FeedbackFilters';
import { mockFeedback, mockProducts, mockCategories } from '../data/mockFeedback';
import { FeedbackFilters as Filters } from '../types/feedback';

export default function FeedbackPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [formData, setFormData] = useState({
    type: '',
    rating: 0,
    feedback: '',
    improvements: '',
    email: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit to an API
    console.log('Feedback submitted:', formData);
    setShowForm(false);
    setFormData({
      type: '',
      rating: 0,
      feedback: '',
      improvements: '',
      email: ''
    });
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-8 w-8 text-green-500" />
            <h1 className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Feedback
            </h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Share Feedback</span>
          </button>
        </div>

        {showForm ? (
          <div className={`
            p-6 rounded-lg mb-8
            ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
          `}>
            <div className={`
              p-4 rounded-lg mb-6 flex items-start space-x-3
              ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}
            `}>
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Your feedback helps us improve our platform. Tell us what you love and what we could do better.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Feedback Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  required
                >
                  <option value="">Select feedback type</option>
                  <option value="general">General Feedback</option>
                  <option value="ui">User Interface</option>
                  <option value="performance">Performance</option>
                  <option value="content">Content Quality</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-4 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Overall Experience
                </label>
                <div className="flex items-center justify-center space-x-8">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating }))}
                      className={`
                        flex flex-col items-center space-y-2 p-3 rounded-lg transition-colors
                        ${formData.rating === rating
                          ? theme === 'dark'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-blue-50 text-blue-600'
                          : theme === 'dark'
                            ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }
                      `}
                    >
                      {rating <= 2 ? (
                        <ThumbsDown className={`h-6 w-6 ${
                          formData.rating === rating ? 'text-red-500' : ''
                        }`} />
                      ) : rating >= 4 ? (
                        <ThumbsUp className={`h-6 w-6 ${
                          formData.rating === rating ? 'text-green-500' : ''
                        }`} />
                      ) : (
                        <div className={`h-6 w-6 rounded-full border-2 ${
                          formData.rating === rating
                            ? 'border-yellow-500'
                            : theme === 'dark'
                              ? 'border-gray-600'
                              : 'border-gray-300'
                        }`} />
                      )}
                      <span className="text-sm">{rating}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Your Feedback
                </label>
                <textarea
                  value={formData.feedback}
                  onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={4}
                  placeholder="Tell us about your experience..."
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Suggested Improvements
                </label>
                <textarea
                  value={formData.improvements}
                  onChange={(e) => setFormData(prev => ({ ...prev, improvements: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  rows={3}
                  placeholder="What could we do better?"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  placeholder="For follow-up questions"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={`
                    px-4 py-2 rounded-lg transition-colors
                    ${theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Feedback
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <FeedbackFilters
              filters={filters}
              onFilterChange={setFilters}
              products={mockProducts}
              categories={mockCategories}
            />
            <FeedbackList items={mockFeedback} type="feedback" />
          </>
        )}
      </div>
    </div>
  );
}